(() => {
  if (window.__TELYSI_PURCHASE_WORKFLOW__) return;
  const RELEASE = '2026-08-27.1';
  const CACHE_TTL = 120000;
  const RETURN_TTL = 15 * 60 * 1000;
  const nativeFetch = window.fetch.bind(window);
  const gid = (type, id) => `gid://shopify/${type}/${String(id).replace(/^gid:\/\/shopify\/[^/]+\//, '')}`;
  const numericId = (value) => Number(String(value || '').split('/').pop());
  const amount = (value) => Number(value || 0);
  const centsToAmount = (value) => Number(value || 0) / 100;
  const returnKey = () => `telysi:purchase-return:${location.pathname}`;

  let checkoutRunning = false;
  let activeLoadingButton = null;
  let selectionVersion = 0;
  let lastSelectionSignature = '';
  let prewarmTimer = 0;
  let cachedCart = null;
  let pendingCart = null;

  const setPhase = (phase) => {
    document.documentElement.dataset.telysiCheckoutPhase = phase;
  };

  const message = (text = '') => {
    let element = document.querySelector('[data-purchase-message]');
    if (!element) {
      element = document.createElement('p');
      element.className = 'reference-purchase-message';
      element.dataset.purchaseMessage = '';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      document.querySelector('.yx-main-product-page')?.after(element);
    }
    if (element) element.textContent = text;
  };

  const productData = () => window.yx_product && typeof window.yx_product === 'object'
    ? window.yx_product
    : {};

  const getSelectedPurchase = (button) => {
    const product = productData();
    const form = document.querySelector('form[data-product-form]');
    const selectedRadio = form?.querySelector('input[data-variant-selector]:checked');
    const variantId = String(
      selectedRadio?.value ||
      form?.querySelector('[data-variant-id-input]')?.value ||
      button?.dataset?.variantId ||
      ''
    );
    const variant = (product.variants || []).find((item) => String(item.id) === variantId);
    if (!variant) throw new Error('The selected bundle could not be read.');

    const quantity = Math.max(1, Number(form?.querySelector('input[name="quantity"]')?.value || 1));
    const inferredUnitCount = Number(String(variant.title || '').match(/\d+/)?.[0] || 1);
    const unitCount = Math.max(1, Number(selectedRadio?.dataset?.unitCount || selectedRadio?.closest('[data-unit-count]')?.dataset?.unitCount || inferredUnitCount));
    const subscription = document.querySelector('input[name="subscription_enabled"][data-selling-plan-id]:checked');
    const sellingPlanId = String(subscription?.dataset?.sellingPlanId || '');
    const allocation = (variant.selling_plan_allocations || []).find(
      (item) => String(item.selling_plan_id) === sellingPlanId
    );
    const finalPrice = Number(allocation?.price ?? variant.price ?? 0);
    const productId = Number(product.id || document.querySelector('.yx-subscription-component[data-product-id]')?.dataset?.productId || 0);
    const productTitle = product.title || document.querySelector('.reference-product__info h1')?.textContent?.trim() || '';
    const variantTitle = variant.public_title || variant.title || '';
    const planName = subscription?.dataset?.sellingPlanName || subscription?.dataset?.deliveryText || 'Delivered every 30 Days | Cancel Anytime';
    const handle = product.handle || location.pathname.split('/products/')[1]?.split(/[?#/]/)[0] || '';
    const image = variant.featured_image?.src || variant.featured_image || product.featured_image || '';
    const sellingPlan = allocation && sellingPlanId ? {
      id: Number(sellingPlanId),
      name: planName,
      groupId: allocation.selling_plan_group_id || '',
    } : null;

    const signature = [variantId, quantity, sellingPlan?.id || 0, unitCount].join(':');
    return {
      signature,
      version: selectionVersion,
      productId,
      productTitle,
      variantId: Number(variantId),
      variantTitle,
      sku: variant.sku || '',
      finalPrice,
      image,
      url: `/products/${handle}?variant=${variantId}`,
      grams: Number(variant.grams || variant.weight || 0),
      currency: window.Shopify?.currency?.active || 'USD',
      quantity,
      unitCount,
      sellingPlan,
    };
  };

  const createIndependentShopifyCart = async (purchase) => {
    const response = await nativeFetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `mutation TelysiCreateIndependentCart($merchandiseId: ID!, $quantity: Int!, $sellingPlanId: ID) {
          cartCreate(input: { lines: [{ merchandiseId: $merchandiseId, quantity: $quantity, sellingPlanId: $sellingPlanId }] }) {
            cart {
              id
              totalQuantity
              cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
              lines(first: 1) {
                nodes {
                  quantity
                  cost { amountPerQuantity { amount currencyCode } totalAmount { amount currencyCode } }
                  merchandise {
                    ... on ProductVariant {
                      id title sku weight weightUnit
                      image { url }
                      product { id title handle }
                    }
                  }
                  sellingPlanAllocation { sellingPlan { id name } }
                }
              }
            }
            userErrors { field message }
          }
        }`,
        variables: {
          merchandiseId: gid('ProductVariant', purchase.variantId),
          quantity: purchase.quantity,
          sellingPlanId: purchase.sellingPlan ? gid('SellingPlan', purchase.sellingPlan.id) : null,
        },
      }),
    });
    const payload = await response.json().catch(() => ({}));
    const result = payload?.data?.cartCreate;
    const errors = [...(payload?.errors || []), ...(result?.userErrors || [])];
    const cart = result?.cart;
    const line = cart?.lines?.nodes?.[0];
    const actualVariantId = numericId(line?.merchandise?.id);
    const actualSellingPlanId = line?.sellingPlanAllocation?.sellingPlan?.id
      ? numericId(line.sellingPlanAllocation.sellingPlan.id)
      : null;
    const expectedSellingPlanId = purchase.sellingPlan?.id || null;

    if (!response.ok || errors.length || !cart?.id || Number(cart.totalQuantity) !== purchase.quantity || Number(line?.quantity) !== purchase.quantity || actualVariantId !== purchase.variantId || actualSellingPlanId !== expectedSellingPlanId) {
      throw new Error(errors.map((error) => error.message).filter(Boolean).join(', ') || 'The independent checkout did not match the selected bundle.');
    }
    return cart;
  };

  const prewarm = (purchase) => {
    const startedAt = Date.now();
    const promise = createIndependentShopifyCart(purchase)
      .then((cart) => {
        if (purchase.signature === getSelectedPurchase()?.signature) {
          cachedCart = { signature: purchase.signature, version: purchase.version, createdAt: Date.now(), cart };
        }
        return cart;
      })
      .catch((error) => {
        console.warn('[Telysi checkout prewarm]', error);
        return null;
      })
      .finally(() => {
        if (pendingCart?.startedAt === startedAt) pendingCart = null;
      });
    pendingCart = { signature: purchase.signature, version: purchase.version, startedAt, promise };
  };

  const schedulePrewarm = (delay = 60) => {
    window.clearTimeout(prewarmTimer);
    prewarmTimer = window.setTimeout(() => {
      try {
        const purchase = getSelectedPurchase();
        if (cachedCart?.signature === purchase.signature && Date.now() - cachedCart.createdAt < CACHE_TTL) return;
        if (pendingCart?.signature === purchase.signature) return;
        prewarm(purchase);
      } catch (error) {
        console.warn('[Telysi checkout selection]', error);
      }
    }, delay);
  };

  const authoritativeCartFor = async (purchase) => {
    if (cachedCart?.signature === purchase.signature && Date.now() - cachedCart.createdAt < CACHE_TTL) return cachedCart.cart;
    if (pendingCart?.signature === purchase.signature) {
      const pending = await pendingCart.promise;
      if (pending) return pending;
    }
    return createIndependentShopifyCart(purchase);
  };

  const buildCheckoutPayload = (purchase, cart) => {
    const line = cart.lines.nodes[0];
    const merchandise = line.merchandise || {};
    const product = merchandise.product || {};
    const unitPrice = amount(line.cost?.amountPerQuantity?.amount || centsToAmount(purchase.finalPrice));
    const linePrice = amount(line.cost?.totalAmount?.amount || unitPrice * line.quantity);
    const currency = line.cost?.totalAmount?.currencyCode || cart.cost?.totalAmount?.currencyCode || purchase.currency;
    const sellingPlanId = line.sellingPlanAllocation?.sellingPlan?.id ? numericId(line.sellingPlanAllocation.sellingPlan.id) : null;
    return {
      cart: {
        items: [{
          variantId: merchandise.id || gid('ProductVariant', purchase.variantId),
          quantity: Number(line.quantity),
          title: merchandise.title && merchandise.title !== 'Default Title' ? `${product.title} - ${merchandise.title}` : (product.title || purchase.productTitle),
          variant: merchandise.title || purchase.variantTitle,
          originalPrice: unitPrice,
          price: unitPrice,
          linePrice,
          totalDiscount: 0,
          productId: product.id || gid('Product', purchase.productId),
          url: `/products/${product.handle || ''}?variant=${purchase.variantId}`,
          image: merchandise.image?.url || purchase.image,
          weight: Number(merchandise.weight || purchase.grams || 0),
          weightUnit: merchandise.weightUnit || 'GRAMS',
          currencyCode: currency,
          sellingPlanId,
          sellingPlanName: line.sellingPlanAllocation?.sellingPlan?.name || purchase.sellingPlan?.name || '',
          sellingPlanGroupId: purchase.sellingPlan?.groupId || '',
        }],
        cartToken: cart.id,
        originalTotalPrice: linePrice,
        totalPrice: amount(cart.cost?.totalAmount?.amount || linePrice),
        totalDiscount: 0,
        totalWeight: Number(merchandise.weight || purchase.grams || 0) * Number(line.quantity),
        itemCount: Number(cart.totalQuantity),
        currency,
      },
      position: { countryCode: window.Shopify?.country || 'US', regionCode: '', city: '' },
      checkoutId: '',
      language: window.Shopify?.locale || 'en',
      extra: {
        view: 'default',
        sourceUrl: `${location.origin}${location.pathname}?view=default&telysiIndependentCheckout=${RELEASE}`,
        telysiIndependentCheckout: RELEASE,
        selectionSignature: purchase.signature,
        cookie_id: '',
      },
    };
  };

  const shopifyId = () => String(window.Shopify?.shop || '').split('.')[0] || 'p1jcfu-c4';
  const checkoutApi = async (path, body) => {
    const response = await nativeFetch(`https://us.ouyifyapi.com${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Shopify-Id': shopifyId(), 'oyAppVersion': 'v2' },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || (payload.code != null && Number(payload.code) !== 200)) throw new Error(payload.message || `Checkout failed (${response.status}).`);
    return payload.data ?? payload;
  };

  const resolvePayPalUrl = (payload) => {
    const data = payload?.data ?? payload ?? {};
    const direct = String(data.paypalPaymentUrl || '').trim();
    if (direct && !/\/thanks|\/paypal\/return|\/confirm-address/i.test(direct)) return direct;
    const links = data.paypalOrder?.links;
    if (Array.isArray(links)) {
      for (const rel of ['payer-action', 'approve']) {
        const url = String(links.find((link) => link?.rel === rel)?.href || '').trim();
        if (/paypal\.com/i.test(url)) return url;
      }
    }
    return String(data.redirectUrl || data.paymentUrl || '').trim();
  };

  const addPayPalFunding = (url) => {
    const target = new URL(url, location.origin);
    if (!target.searchParams.has('useraction')) target.searchParams.set('useraction', 'commit');
    if (!target.searchParams.has('fundingSource')) target.searchParams.set('fundingSource', 'paypal');
    return target.href;
  };

  const ensureOverlayStyles = () => {
    if (document.querySelector('#telysi-processing-styles')) return;
    const style = document.createElement('style');
    style.id = 'telysi-processing-styles';
    style.textContent = `
      html.telysi-checkout-processing,html.telysi-checkout-processing body{overflow:hidden!important}
      .telysi-processing-overlay{position:fixed;inset:0;z-index:2147483000;display:grid;min-height:100dvh;place-items:center;background:rgba(255,255,255,.9);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px)}
      .telysi-processing-card{display:flex;flex-direction:column;align-items:center;gap:24px;transform:translateY(-3vh)}
      .telysi-processing-visual{position:relative;display:grid;width:178px;height:178px;place-items:center;color:var(--product-green,#0f6b4d)}
      .telysi-processing-ring{position:absolute;inset:0;border:4px solid currentColor;border-top-color:transparent;border-left-color:transparent;border-radius:50%;animation:telysi-processing-spin 1.05s linear infinite}
      .telysi-processing-bag{width:62px;height:62px;fill:none;stroke:currentColor;stroke-width:4;stroke-linecap:round;stroke-linejoin:round}
      .telysi-processing-label{color:var(--product-black,#101714);font-family:Arial,Helvetica,sans-serif;font-size:clamp(28px,3vw,38px);font-weight:800;line-height:1}
      .telysi-processing-dot{display:inline-block;animation:telysi-processing-dot 1.05s ease-in-out infinite}
      @keyframes telysi-processing-spin{to{transform:rotate(360deg)}}@keyframes telysi-processing-dot{0%,35%{opacity:.25}65%,100%{opacity:1}}
      @media(max-width:480px){.telysi-processing-visual{width:150px;height:150px}.telysi-processing-bag{width:54px;height:54px}}
      @media(prefers-reduced-motion:reduce){.telysi-processing-ring{animation-duration:1.8s}}
    `;
    document.head.append(style);
  };

  const showOverlay = () => {
    ensureOverlayStyles();
    document.querySelector('#telysi-processing-overlay')?.remove();
    const overlay = document.createElement('div');
    overlay.id = 'telysi-processing-overlay';
    overlay.className = 'telysi-processing-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'assertive');
    overlay.setAttribute('aria-label', 'Processing secure checkout');
    overlay.innerHTML = `<div class="telysi-processing-card"><div class="telysi-processing-visual" aria-hidden="true"><span class="telysi-processing-ring"></span><svg class="telysi-processing-bag" viewBox="0 0 64 64" focusable="false"><path d="M19 26h26l3 27c.3 3-2 5-5 5H21c-3 0-5.3-2-5-5l3-27Z"></path><path d="M25 30v-8a7 7 0 0 1 14 0v8"></path></svg></div><div class="telysi-processing-label">Processing<span class="telysi-processing-dot" aria-hidden="true">.</span></div></div>`;
    document.documentElement.classList.add('telysi-checkout-processing');
    document.body.append(overlay);
  };

  const hideOverlay = () => {
    document.querySelector('#telysi-processing-overlay')?.remove();
    document.documentElement.classList.remove('telysi-checkout-processing');
  };

  const setLoading = (button) => {
    if (!button) return;
    activeLoadingButton = button;
    button.dataset.telysiOriginalHtml = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = button.id === 'yx_paypal_checkout' ? 'CONNECTING TO PAYPAL…' : 'LOADING SECURE CHECKOUT…';
    showOverlay();
  };

  const resetLoading = (button) => {
    if (!button) return;
    if (button.dataset.telysiOriginalHtml) button.innerHTML = button.dataset.telysiOriginalHtml;
    delete button.dataset.telysiOriginalHtml;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (activeLoadingButton === button) activeLoadingButton = null;
    hideOverlay();
  };

  const clearProcessingState = () => {
    new Set([activeLoadingButton, ...document.querySelectorAll('[data-telysi-original-html]')].filter(Boolean)).forEach(resetLoading);
    activeLoadingButton = null;
    checkoutRunning = false;
    setPhase('idle');
    hideOverlay();
  };

  const saveReturnState = (purchase) => {
    sessionStorage.setItem(returnKey(), JSON.stringify({
      savedAt: Date.now(),
      variantId: purchase.variantId,
      quantity: purchase.quantity,
      subscriptionChecked: Boolean(purchase.sellingPlan),
    }));
  };

  const restoreReturnState = () => {
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(returnKey()) || 'null'); } catch (_) { saved = null; }
    if (!saved || Date.now() - Number(saved.savedAt || 0) > RETURN_TTL) {
      sessionStorage.removeItem(returnKey());
      return;
    }
    const apply = () => {
      const radio = document.querySelector(`input[data-variant-selector][value="${saved.variantId}"]`);
      if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      const quantity = document.querySelector('form[data-product-form] input[name="quantity"]');
      if (quantity) { quantity.value = Math.max(1, Number(saved.quantity || 1)); quantity.dispatchEvent(new Event('change', { bubbles: true })); }
      const subscription = document.querySelector('input[name="subscription_enabled"][data-selling-plan-id]');
      if (subscription) {
        subscription.checked = Boolean(saved.subscriptionChecked);
        subscription.dispatchEvent(new Event('change', { bubbles: true }));
        sessionStorage.removeItem(returnKey());
        schedulePrewarm(0);
        return true;
      }
      if (!saved.subscriptionChecked) {
        sessionStorage.removeItem(returnKey());
        schedulePrewarm(0);
        return true;
      }
      return false;
    };
    if (apply()) return;
    const observer = new MutationObserver(() => { if (apply()) observer.disconnect(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 8000);
  };

  const startIndependentCheckout = async (button) => {
    if (checkoutRunning) return;
    checkoutRunning = true;
    let navigationStarted = false;
    message('');
    setPhase('preparing');
    setLoading(button);
    try {
      const purchase = getSelectedPurchase(button);
      const cart = await authoritativeCartFor(purchase);
      if (purchase.signature !== getSelectedPurchase(button).signature) throw new Error('Your selection changed. Please review it and try again.');
      const checkoutPayload = buildCheckoutPayload(purchase, cart);
      setPhase('connecting');

      if (button?.id === 'yx_paypal_checkout') {
        const created = await checkoutApi('/api/checkout/paypal', checkoutPayload);
        const paymentUrl = resolvePayPalUrl(created);
        if (!paymentUrl) throw new Error('PayPal payment URL was not returned.');
        saveReturnState(purchase);
        setPhase('navigating');
        navigationStarted = true;
        window.location.assign(addPayPalFunding(paymentUrl));
        return;
      }

      const checkout = await checkoutApi('/api/checkout', checkoutPayload);
      if (!checkout.checkoutId) throw new Error('Checkout ID was not returned.');
      saveReturnState(purchase);
      setPhase('navigating');
      navigationStarted = true;
      window.location.assign(`/a/s/checkout/${checkout.checkoutId}`);
    } catch (error) {
      console.error('[Telysi independent checkout]', error);
      setPhase('error');
      message(error?.message || 'Checkout could not be started. Please try again.');
    } finally {
      checkoutRunning = false;
      if (!navigationStarted) {
        resetLoading(button);
        window.setTimeout(() => setPhase('idle'), 0);
      }
    }
  };

  const selectionChanged = () => {
    let nextSignature = '';
    try { nextSignature = getSelectedPurchase().signature; } catch {}
    if (nextSignature && nextSignature === lastSelectionSignature) return;
    lastSelectionSignature = nextSignature;
    selectionVersion += 1;
    cachedCart = null;
    message('');
    window.requestAnimationFrame(decorateBundles);
    schedulePrewarm();
  };

  const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  }).format(Number(cents || 0) / 100);

  const decorateBundles = () => {
    const product = productData();
    const subscription = document.querySelector('input[name="subscription_enabled"][data-selling-plan-id]');
    document.querySelectorAll('input[data-variant-selector]').forEach((input) => {
      const variant = (product.variants || []).find((item) => String(item.id) === String(input.value));
      if (!variant) return;
      const unitCount = Math.max(1, Number(input.dataset.unitCount || String(variant.title || '').match(/\d+/)?.[0] || 1));
      input.dataset.unitCount = String(unitCount);
      input.closest('.reference-bundle')?.setAttribute('data-unit-count', String(unitCount));
      const allocation = subscription?.checked
        ? (variant.selling_plan_allocations || []).find((item) => String(item.selling_plan_id) === String(subscription.dataset.sellingPlanId || ''))
        : null;
      const total = Number(allocation?.price ?? variant.price ?? 0);
      const price = input.closest('.reference-bundle')?.querySelector('.reference-bundle__prices');
      const markup = `<strong data-unit-price>${formatMoney(Math.round(total / unitCount))} <small>/ piece</small></strong><span data-total-price>Total: ${formatMoney(total)}</span>`;
      if (price && price.innerHTML !== markup) price.innerHTML = markup;
    });
    const variantId = document.querySelector('form[data-product-form] [data-variant-id-input]')?.value || document.querySelector('input[data-variant-selector]:checked')?.value || '';
    document.querySelectorAll('.yx-main-product-page [data-variant-id]').forEach((button) => { button.dataset.variantId = variantId; });
  };

  const installSurfaceRepair = () => {
    if (!document.querySelector('#telysi-surface-repair-styles')) {
      const style = document.createElement('style');
      style.id = 'telysi-surface-repair-styles';
      style.textContent = `
        .cart-icon,.reference-add-to-cart{display:none!important}
        .reference-bundle__prices{display:grid!important;min-width:102px!important;gap:2px!important;text-align:right!important}
        .reference-bundle__prices strong{color:var(--product-green,#0f6b4d)!important;font-size:1.02rem!important;white-space:nowrap!important}
        .reference-bundle__prices strong small{font-size:.62rem!important;font-weight:800!important}
        .reference-bundle__prices>span{color:var(--product-muted,#68746d)!important;font-size:.68rem!important;white-space:nowrap!important}
        .reference-purchase-message{min-height:1.25em;margin:8px 0 0;color:#a12d24;font-size:.78rem;line-height:1.45}.reference-purchase-message:empty{display:none}
        @media(max-width:749px){.reference-bundle{grid-template-columns:20px minmax(0,1fr) minmax(92px,auto)!important;gap:8px!important;padding:13px 10px!important}.reference-bundle__prices{min-width:92px!important}.reference-bundle__prices strong{font-size:.9rem!important}.reference-bundle__prices>span{font-size:.62rem!important}}
        @media(max-width:360px){.reference-bundle{grid-template-columns:18px minmax(0,1fr) minmax(84px,auto)!important;gap:6px!important;padding:12px 8px!important}.reference-bundle__prices{min-width:84px!important}.reference-bundle__prices strong{font-size:.82rem!important}.reference-bundle__prices>span{font-size:.58rem!important}}
      `;
      document.head.append(style);
    }
    document.querySelectorAll('.cart-icon,.reference-add-to-cart').forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('tabindex', '-1');
    });
    decorateBundles();
  };

  const bindSelectionWatchers = () => {
    document.addEventListener('change', (event) => {
      if (event.target.matches?.('input[data-variant-selector], input[name="quantity"], input[name="subscription_enabled"]')) selectionChanged();
    }, true);
    document.addEventListener('input', (event) => {
      if (event.target.matches?.('input[name="quantity"]')) selectionChanged();
    }, true);
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-quantity] button')) window.setTimeout(selectionChanged, 0);
    }, true);
  };

  window.addEventListener('pagehide', clearProcessingState);
  window.addEventListener('pageshow', clearProcessingState);
  window.__TELYSI_PURCHASE_WORKFLOW__ = RELEASE;
  window.__BIDANK_START_BUY_NOW__ = startIndependentCheckout;
  document.documentElement.dataset.bidankBuyNowHandler = 'independent-selected-bundle';
  document.documentElement.dataset.telysiPurchaseWorkflow = RELEASE;
  setPhase('idle');
  window.dispatchEvent(new CustomEvent('bidank:buy-now-ready', { detail: startIndependentCheckout }));

  const init = () => {
    installSurfaceRepair();
    bindSelectionWatchers();
    restoreReturnState();
    try { lastSelectionSignature = getSelectedPurchase().signature; } catch {}
    schedulePrewarm(0);
    window.__BIDANK_START_BUY_NOW__ = startIndependentCheckout;
    window.dispatchEvent(new CustomEvent('bidank:buy-now-ready', { detail: startIndependentCheckout }));
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('bidank:buy-now-ready', { detail: startIndependentCheckout })), 500);
    const observer = new MutationObserver(() => window.requestAnimationFrame(installSurfaceRepair));
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
